/**
 * BBT Searchable Filter Dropdowns + Cascading Make→Model
 *
 * 1. Adds type-ahead search to filter lists with many options
 * 2. Cascades the Model filter based on selected Makes using a JSON mapping
 *
 * Designed for Meka theme (drawer filter mode) + standard Shopify filter markup.
 */
(function () {
  "use strict";

  var MIN_OPTIONS = 10;
  var DEBOUNCE_MS = 150;
  var CLS = "bbt-sf";

  // Make→Model mapping — loaded async from theme asset
  var makeModelsMap = null;
  var mapLoaded = false;

  // Flexible matching: Shopify filter param names for metaobject-based filters
  // could be filter.p.m.fitment.makes, filter.p.m.custom.fitment.makes, etc.
  // We match any name containing these substrings
  var MAKE_KEYWORDS = ["fitment.makes", "fitment_makes", "vehicle_make"];
  var MODEL_KEYWORDS = ["fitment.models", "fitment_models", "vehicle_model"];

  function nameMatchesMake(name) {
    if (!name) return false;
    var lower = name.toLowerCase();
    for (var i = 0; i < MAKE_KEYWORDS.length; i++) {
      if (lower.indexOf(MAKE_KEYWORDS[i]) >= 0) return true;
    }
    return false;
  }

  function nameMatchesModel(name) {
    if (!name) return false;
    var lower = name.toLowerCase();
    for (var i = 0; i < MODEL_KEYWORDS.length; i++) {
      if (lower.indexOf(MODEL_KEYWORDS[i]) >= 0) return true;
    }
    return false;
  }

  // Also detect by the <details> heading / summary text
  function detailsLabelMatches(detailsEl, keywords) {
    if (!detailsEl) return false;
    var summary = detailsEl.querySelector("summary");
    if (!summary) return false;
    var text = summary.textContent.toLowerCase().trim();
    for (var i = 0; i < keywords.length; i++) {
      if (text.indexOf(keywords[i]) >= 0) return true;
    }
    return false;
  }

  // ─── Load make-models mapping ───────────────────────────────────

  function loadMakeModelsMap() {
    var assetUrl = null;
    var linkTags = document.querySelectorAll('link[rel="stylesheet"][href*="bbt-searchable-filters"]');
    if (linkTags.length > 0) {
      assetUrl = linkTags[0].href.replace("bbt-searchable-filters.css", "bbt-make-models-map.json");
    }
    if (!assetUrl) {
      var scripts = document.querySelectorAll('script[src*="bbt-searchable-filters"]');
      if (scripts.length > 0) {
        assetUrl = scripts[0].src.replace("bbt-searchable-filters.js", "bbt-make-models-map.json");
      }
    }
    if (!assetUrl) return;

    var xhr = new XMLHttpRequest();
    xhr.open("GET", assetUrl, true);
    xhr.onload = function () {
      if (xhr.status === 200) {
        try {
          makeModelsMap = JSON.parse(xhr.responseText);
          mapLoaded = true;
          // Apply cascading immediately — handles page-load case where
          // Makes are pre-selected from URL params
          applyCascading();
        } catch (e) {
          // Mapping load failed — cascading disabled
        }
      }
    };
    xhr.send();
  }

  // ─── Cascading Make → Model logic ──────────────────────────────

  /**
   * Find all checked Make filter checkboxes across all filter forms
   * (desktop, mobile/drawer, pills) and return their labels.
   */
  function getSelectedMakes() {
    var makes = [];
    // Find all checkboxes in the page
    var allCheckboxes = document.querySelectorAll('input[type="checkbox"]:checked');
    for (var i = 0; i < allCheckboxes.length; i++) {
      var cb = allCheckboxes[i];
      var name = cb.getAttribute("name") || "";
      if (nameMatchesMake(name)) {
        var label = cb.getAttribute("data-filter-value-name");
        if (label && makes.indexOf(label) === -1) {
          makes.push(label);
        }
      }
    }

    // Also check URL params as fallback (for initial page load before DOM is fully rendered)
    if (makes.length === 0) {
      var params = new URLSearchParams(window.location.search);
      params.forEach(function (value, key) {
        if (nameMatchesMake(key) && makes.indexOf(value) === -1) {
          makes.push(value);
        }
      });
    }

    return makes;
  }

  /**
   * Get the set of allowed model labels for the given selected makes.
   * Returns null if no makes are selected (meaning: show all).
   */
  function getAllowedModels(selectedMakes) {
    if (!makeModelsMap || selectedMakes.length === 0) return null;

    var allowed = {};
    for (var i = 0; i < selectedMakes.length; i++) {
      var makeName = selectedMakes[i];
      // Try exact match first, then case-insensitive
      var models = makeModelsMap[makeName];
      if (!models) {
        // Case-insensitive fallback
        var makeKeys = Object.keys(makeModelsMap);
        for (var k = 0; k < makeKeys.length; k++) {
          if (makeKeys[k].toLowerCase() === makeName.toLowerCase()) {
            models = makeModelsMap[makeKeys[k]];
            break;
          }
        }
      }
      if (models) {
        for (var j = 0; j < models.length; j++) {
          allowed[models[j]] = true;
          // Also allow case-insensitive match
          allowed[models[j].toLowerCase()] = true;
        }
      }
    }
    return allowed;
  }

  /**
   * Apply cascading: hide/show Model filter items based on selected Makes.
   * Works on all filter forms (desktop, mobile/drawer, pills).
   */
  function applyCascading() {
    if (!makeModelsMap) return;

    var selectedMakes = getSelectedMakes();
    var allowedModels = getAllowedModels(selectedMakes);

    // Find ALL checkboxes across all filter forms
    var allCheckboxes = document.querySelectorAll('input[type="checkbox"]');

    for (var i = 0; i < allCheckboxes.length; i++) {
      var cb = allCheckboxes[i];
      var name = cb.getAttribute("name") || "";

      // Only process Model filter checkboxes
      if (!nameMatchesModel(name)) continue;

      var modelLabel = cb.getAttribute("data-filter-value-name");
      var li = cb.closest("li");
      if (!li) continue;

      if (allowedModels === null) {
        // No make selected — show all models
        li.style.display = "";
        li.dataset.bbtCascadeHidden = "";
      } else if (modelLabel && (allowedModels[modelLabel] || allowedModels[modelLabel.toLowerCase()])) {
        // Model belongs to a selected make — show it
        li.style.display = "";
        li.dataset.bbtCascadeHidden = "";
      } else {
        // Model does NOT belong to any selected make — hide it
        li.style.display = "none";
        li.dataset.bbtCascadeHidden = "1";
      }
    }
  }

  /**
   * Listen for ANY checkbox change and apply cascading if it's a Make filter.
   * Uses event delegation on document for robustness against DOM replacements.
   */
  function setupCascadeListeners() {
    // Use 'input' event to match Shopify's own listener (line 11 of facets.js)
    // and also 'change' as backup
    document.addEventListener("input", onFilterInput, true);
    document.addEventListener("change", onFilterInput, true);
  }

  function onFilterInput(e) {
    var target = e.target;
    if (!target || target.type !== "checkbox") return;
    var name = target.getAttribute("name") || "";
    if (nameMatchesMake(name)) {
      // Apply cascading immediately
      applyCascading();
      // Auto-collapse Make, auto-expand Model after a brief moment
      // so the user sees the tick register before the panel switches
      setTimeout(function () {
        autoSwitchToModel(target);
      }, 250);
    }
  }

  /**
   * After ticking a Make checkbox, collapse the Make <details> and
   * expand the Model <details> for smoother UX.
   */
  function autoSwitchToModel(makeCheckbox) {
    // Find the Make <details> ancestor
    var makeDetails = makeCheckbox.closest("details");
    if (!makeDetails) return;

    // Find the Model <details> sibling — look through all <details> in the same form
    var form = makeDetails.closest("form") || makeDetails.parentNode;
    var allDetails = form.querySelectorAll("details.js-filter, details.mobile-facets__details");
    var modelDetails = null;

    for (var i = 0; i < allDetails.length; i++) {
      var cb = allDetails[i].querySelector('input[type="checkbox"]');
      if (cb && nameMatchesModel(cb.getAttribute("name") || "")) {
        modelDetails = allDetails[i];
        break;
      }
    }

    if (!modelDetails) return;

    // Collapse Make
    makeDetails.removeAttribute("open");

    // Expand Model
    modelDetails.setAttribute("open", "");

    // Meka drawer uses a submenu slide pattern — also click the summary
    // to trigger the theme's own open/close animations
    var modelSummary = modelDetails.querySelector("summary");
    if (modelSummary && !modelDetails.open) {
      modelSummary.click();
    }
  }

  // ─── Select dropdown enhancement (for themes using <select>) ───

  function shouldEnhance(select) {
    if (select.dataset.bbtEnhanced) return false;
    if (select.options.length < MIN_OPTIONS) return false;
    if (select.offsetParent === null && !select.closest("[data-filter]")) return false;
    return true;
  }

  function buildHighlightedText(parent, fullText, filterLower) {
    while (parent.firstChild) parent.removeChild(parent.firstChild);

    if (!filterLower) {
      parent.appendChild(document.createTextNode(fullText));
      return;
    }

    var lowerFull = fullText.toLowerCase();
    var idx = lowerFull.indexOf(filterLower);

    if (idx < 0) {
      parent.appendChild(document.createTextNode(fullText));
      return;
    }

    if (idx > 0) {
      parent.appendChild(document.createTextNode(fullText.substring(0, idx)));
    }

    var mark = document.createElement("mark");
    mark.textContent = fullText.substring(idx, idx + filterLower.length);
    parent.appendChild(mark);

    var afterIdx = idx + filterLower.length;
    if (afterIdx < fullText.length) {
      parent.appendChild(document.createTextNode(fullText.substring(afterIdx)));
    }
  }

  function enhance(select) {
    select.dataset.bbtEnhanced = "1";

    var options = [];
    for (var i = 0; i < select.options.length; i++) {
      var opt = select.options[i];
      options.push({
        value: opt.value,
        label: opt.textContent.trim(),
        lowerLabel: opt.textContent.trim().toLowerCase(),
        selected: opt.selected,
      });
    }

    var wrapper = document.createElement("div");
    wrapper.className = CLS + "-wrapper";

    var trigger = document.createElement("button");
    trigger.type = "button";
    trigger.className = CLS + "-trigger";
    var selectedOpt = options.find(function (o) { return o.selected; }) || options[0];
    trigger.textContent = selectedOpt ? selectedOpt.label : "Select...";
    trigger.setAttribute("aria-haspopup", "listbox");
    trigger.setAttribute("aria-expanded", "false");

    var dropdown = document.createElement("div");
    dropdown.className = CLS + "-dropdown";
    dropdown.style.display = "none";

    var searchInput = document.createElement("input");
    searchInput.type = "text";
    searchInput.className = CLS + "-search";
    searchInput.placeholder = "Type to search\u2026";
    searchInput.setAttribute("autocomplete", "off");
    searchInput.setAttribute("aria-label", "Search filter options");

    var listEl = document.createElement("ul");
    listEl.className = CLS + "-list";
    listEl.setAttribute("role", "listbox");

    function renderOptions(filter) {
      var filterLower = (filter || "").toLowerCase();
      while (listEl.firstChild) listEl.removeChild(listEl.firstChild);
      var count = 0;

      for (var j = 0; j < options.length; j++) {
        var opt = options[j];
        if (filterLower && opt.lowerLabel.indexOf(filterLower) === -1) continue;

        var li = document.createElement("li");
        li.className = CLS + "-option";
        if (opt.selected) li.classList.add(CLS + "-option--selected");
        li.setAttribute("role", "option");
        li.setAttribute("data-value", opt.value);
        li.setAttribute("tabindex", "-1");
        buildHighlightedText(li, opt.label, filterLower);

        li.addEventListener("click", (function (o) {
          return function () { selectOption(o); };
        })(opt));

        listEl.appendChild(li);
        count++;
      }

      if (count === 0) {
        var empty = document.createElement("li");
        empty.className = CLS + "-empty";
        empty.textContent = "No matches";
        listEl.appendChild(empty);
      }
    }

    function selectOption(opt) {
      select.value = opt.value;
      for (var k = 0; k < options.length; k++) {
        options[k].selected = options[k].value === opt.value;
      }
      trigger.textContent = opt.label;
      closeDropdown();

      var event;
      if (typeof Event === "function") {
        event = new Event("change", { bubbles: true });
      } else {
        event = document.createEvent("Event");
        event.initEvent("change", true, true);
      }
      select.dispatchEvent(event);
    }

    function openDropdown() {
      dropdown.style.display = "";
      trigger.setAttribute("aria-expanded", "true");
      wrapper.classList.add(CLS + "-wrapper--open");
      searchInput.value = "";
      renderOptions("");
      var selectedEl = listEl.querySelector("." + CLS + "-option--selected");
      if (selectedEl) selectedEl.scrollIntoView({ block: "nearest" });
      searchInput.focus();
    }

    function closeDropdown() {
      dropdown.style.display = "none";
      trigger.setAttribute("aria-expanded", "false");
      wrapper.classList.remove(CLS + "-wrapper--open");
    }

    function toggleDropdown() {
      if (dropdown.style.display === "none") openDropdown();
      else closeDropdown();
    }

    trigger.addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      toggleDropdown();
    });

    var debounceTimer = null;
    searchInput.addEventListener("input", function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        renderOptions(searchInput.value);
      }, DEBOUNCE_MS);
    });

    searchInput.addEventListener("keydown", function (e) {
      if (e.key === "Escape") { closeDropdown(); trigger.focus(); }
      else if (e.key === "ArrowDown") { e.preventDefault(); var f = listEl.querySelector("." + CLS + "-option"); if (f) f.focus(); }
      else if (e.key === "Enter") { e.preventDefault(); var fo = listEl.querySelector("." + CLS + "-option"); if (fo) fo.click(); }
    });

    listEl.addEventListener("keydown", function (e) {
      var focused = document.activeElement;
      if (e.key === "ArrowDown" && focused && focused.nextElementSibling) { e.preventDefault(); focused.nextElementSibling.focus(); }
      else if (e.key === "ArrowUp" && focused && focused.previousElementSibling) { e.preventDefault(); focused.previousElementSibling.focus(); }
      else if (e.key === "Escape") { closeDropdown(); trigger.focus(); }
    });

    document.addEventListener("click", function (e) {
      if (!wrapper.contains(e.target)) closeDropdown();
    });

    dropdown.appendChild(searchInput);
    dropdown.appendChild(listEl);
    wrapper.appendChild(trigger);
    wrapper.appendChild(dropdown);

    select.style.position = "absolute";
    select.style.opacity = "0";
    select.style.pointerEvents = "none";
    select.style.width = "0";
    select.style.height = "0";
    select.style.overflow = "hidden";
    select.setAttribute("tabindex", "-1");
    select.setAttribute("aria-hidden", "true");

    select.parentNode.insertBefore(wrapper, select);
    renderOptions("");
  }

  // ─── Checkbox list search enhancement ──────────────────────────

  function enhanceCheckboxList(container) {
    if (container.dataset.bbtEnhanced) return;
    container.dataset.bbtEnhanced = "1";

    var items = container.querySelectorAll("li.mobile-facets__item, li.list-menu__item, li.facets__item");
    if (items.length === 0) items = container.querySelectorAll("li");
    if (items.length < MIN_OPTIONS) return;

    // Detect if this is a Model filter list (for cascade-aware search)
    var isModelList = false;
    var firstCb = container.querySelector('input[type="checkbox"]');
    if (firstCb) {
      var cbName = firstCb.getAttribute("name") || "";
      isModelList = nameMatchesModel(cbName);
    }

    var searchInput = document.createElement("input");
    searchInput.type = "text";
    searchInput.className = CLS + "-search " + CLS + "-search--inline";
    searchInput.placeholder = "Type to search\u2026";
    searchInput.setAttribute("autocomplete", "off");
    searchInput.setAttribute("aria-label", "Search filter options");

    searchInput.addEventListener("click", function (e) {
      e.stopPropagation();
    });

    var debounceTimer = null;
    searchInput.addEventListener("input", function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        var query = searchInput.value.toLowerCase();
        for (var i = 0; i < items.length; i++) {
          // Skip items hidden by cascading (make→model)
          if (isModelList && items[i].dataset.bbtCascadeHidden === "1") {
            items[i].style.display = "none";
            continue;
          }

          var text = items[i].textContent.toLowerCase();
          var matches = !query || text.indexOf(query) >= 0;
          items[i].style.display = matches ? "" : "none";
          if (matches && query) {
            items[i].classList.remove("hidden");
          }
        }
        var showMoreBtn = container.parentNode.querySelector(".button-show-more");
        if (showMoreBtn) {
          showMoreBtn.style.display = query ? "none" : "";
        }
      }, DEBOUNCE_MS);
    });

    container.insertBefore(searchInput, container.firstChild);
  }

  // ─── DOM scanning ──────────────────────────────────────────────

  function scanAndEnhance() {
    var selects = document.querySelectorAll("select");
    for (var i = 0; i < selects.length; i++) {
      if (shouldEnhance(selects[i])) enhance(selects[i]);
    }

    var filterContainers = document.querySelectorAll(
      ".mobile-facets__submenu ul.mobile-facets__list," +
      "ul.facets__list--vertical," +
      "ul.facets__list," +
      "[data-filter-group] ul," +
      ".filter-slide__list"
    );
    for (var j = 0; j < filterContainers.length; j++) {
      enhanceCheckboxList(filterContainers[j]);
    }

    // Re-apply cascading after DOM changes (AJAX re-render)
    if (mapLoaded) applyCascading();
  }

  // ─── Initialize ────────────────────────────────────────────────

  function init() {
    setupCascadeListeners();
    loadMakeModelsMap();
    scanAndEnhance();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Watch for AJAX-loaded filter content (Shopify re-renders filters on every change)
  if (typeof MutationObserver !== "undefined") {
    var observer = new MutationObserver(function (mutations) {
      var shouldRescan = false;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length > 0) {
          shouldRescan = true;
          break;
        }
      }
      if (shouldRescan) {
        clearTimeout(observer._bbtTimer);
        observer._bbtTimer = setTimeout(scanAndEnhance, 100);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
})();
